// src/pages/LogsPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getLatestLines, getLogInfo, makeDownloadHref, makeStreamUrl } from "../api/logs";
import { useSSE } from "../hooks/useSSE";

const LEVELS = ["all", "error", "warn", "info", "debug", "trace"];

const LEVEL_PATTERNS = {
  error: /\berror\b|\bERR\b|\[ERROR\]/i,
  warn: /\bwarn(ing)?\b|\[WARN\]/i,
  info: /\binfo\b|\[INFO\]/i,
  debug: /\bdebug\b|\[DEBUG\]/i,
  trace: /\btrace\b|\[TRACE\]/i,
};

function matchLevel(line, level) {
  if (level === "all") return true;
  const rx = LEVEL_PATTERNS[level];
  return rx ? rx.test(line) : true;
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlight(line, needle) {
  if (!needle) return line;
  const parts = line.split(new RegExp(`(${escapeRegExp(needle)})`, "gi"));
  return parts.map((p, i) =>
    p.toLowerCase() === needle.toLowerCase() ? (
      <mark key={i} style={{ background: "rgba(234, 179, 8, .35)" }}>{p}</mark>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
}

export default function LogsPage() {
  const [info, setInfo] = useState({ path: undefined, exists: undefined });
  const [lines, setLines] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [level, setLevel] = useState("all");
  const [autoscroll, setAutoscroll] = useState(true);
  const [paused, setPaused] = useState(false);
  const [initialN, setInitialN] = useState(500);
  const [loading, setLoading] = useState(false);

  const viewRef = useRef(null);

  // Headers con token (como axios), para SSE:
  const token = localStorage.getItem("token") || "";
  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : undefined),
    [token]
  );

  const onMessage = useCallback((evt) => {
    const text = typeof evt === "string" ? evt : String((evt && evt.line) || "");
    if (!text) return;
    setLines((prev) => [...prev, text]);
  }, []);

  const onError = useCallback((err) => {
    // eslint-disable-next-line no-console
    console.error("[SSE] error:", err);
  }, []);

  const streamUrl = makeStreamUrl(); // incluye ?t=token
  const { connected, pause, resume } = useSSE({
    url: streamUrl,
    headers, // también Authorization
    onMessage,
    onError,
    onOpen: () => console.log("[SSE] connected"),
    enabled: !paused,
  });

  // Autoscroll
  useEffect(() => {
    if (!autoscroll) return;
    const el = viewRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [lines, autoscroll]);

  // Carga inicial + info
  const loadInitial = useCallback(async () => {
    try {
      setLoading(true);
      const [i, l] = await Promise.all([getLogInfo(), getLatestLines(initialN)]);
      setInfo({ path: i && i.path, exists: i && i.exists });
      setLines(Array.isArray(l) ? l : []);
    } catch (e) {
      console.error("loadInitial error:", e);
    } finally {
      setLoading(false);
    }
  }, [initialN]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const filtered = useMemo(() => {
    const t = filterText.trim().toLowerCase();
    if (!t && level === "all") return lines;
    return lines.filter((ln) => {
      if (t && !String(ln).toLowerCase().includes(t)) return false;
      if (!matchLevel(ln, level)) return false;
      return true;
    });
  }, [lines, filterText, level]);

  const handlePause = () => {
    if (!paused) {
      pause();
      setPaused(true);
    } else {
      setPaused(false);
      resume();
    }
  };

  const handleClear = () => setLines([]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(filtered.join("\n"));
    } catch {}
  };

  const downloadHref = makeDownloadHref(); // incluye ?t=token

  return (
    <div style={styles.page}>
      <div style={styles.toolbar}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <strong style={{ fontSize: 16 }}>Logs</strong>
          <span style={styles.badge}>
            {connected && !paused ? "Conectado" : paused ? "Pausado" : "Reconectando…"}
          </span>
          {info && info.path ? (
            <span title={info.path} style={{ color: "#64748b", fontSize: 12 }}>
              {info.path}
            </span>
          ) : null}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select value={level} onChange={(e) => setLevel(e.target.value)} style={styles.input} aria-label="Nivel">
            {LEVELS.map((lv) => (
              <option key={lv} value={lv}>
                {lv === "all" ? "Nivel: Todos" : lv[0].toUpperCase() + lv.slice(1)}
              </option>
            ))}
          </select>

          <input
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Filtrar texto…"
            style={{ ...styles.input, width: 220 }}
          />

          <label style={styles.switchLabel}>
            <input type="checkbox" checked={autoscroll} onChange={(e) => setAutoscroll(e.target.checked)} />
            <span>Autoscroll</span>
          </label>

          <button onClick={handlePause} style={styles.btn}>{paused ? "Reanudar" : "Pausar"}</button>
          <button onClick={handleClear} style={styles.btnSecondary}>Limpiar</button>
          <button onClick={copyToClipboard} style={styles.btnSecondary}>Copiar</button>
          <a href={downloadHref} style={styles.btn} rel="noreferrer" target="_blank">Descargar</a>
        </div>
      </div>

      <div style={styles.subToolbar}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: 12, color: "#94a3b8" }}>
            Carga inicial:
            <input
              type="number"
              min={50}
              max={5000}
              step={50}
              value={initialN}
              onChange={(e) => setInitialN(Math.max(50, Math.min(5000, Number(e.target.value) || 500)))}
              style={{ ...styles.input, width: 90, marginLeft: 6 }}
            />
            <button onClick={loadInitial} style={{ ...styles.btnSecondary, marginLeft: 6 }}>Recargar últimas</button>
          </label>
          {loading && <span style={styles.badge}>Cargando…</span>}
        </div>
      </div>

      <div ref={viewRef} style={styles.viewer} role="log" aria-live="polite">
        {filtered.length === 0 ? (
          <div style={{ color: "#94a3b8", fontSize: 13 }}>Sin líneas para mostrar.</div>
        ) : (
          filtered.map((ln, i) => (
            <pre key={`${i}-${String(ln).length}`} style={styles.line}>
              {highlight(String(ln), filterText)}
            </pre>
          ))
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { display: "flex", flexDirection: "column", gap: 8, padding: 16, height: "100%", boxSizing: "border-box" },
  toolbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 12px",
    border: "1px solid #e2e8f0", borderRadius: 10, background: "#fff"
  },
  subToolbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "6px 10px",
    border: "1px solid #e2e8f0", borderRadius: 10, background: "#f8fafc"
  },
  input: { border: "1px solid #cbd5e1", borderRadius: 8, padding: "6px 10px", outline: "none", background: "#fff" },
  btn: { border: "1px solid #0ea5e9", background: "#0ea5e9", color: "white", borderRadius: 8, padding: "6px 10px", cursor: "pointer" },
  btnSecondary: { border: "1px solid #cbd5e1", background: "#fff", color: "#0f172a", borderRadius: 8, padding: "6px 10px", cursor: "pointer" },
  switchLabel: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155" },
  viewer: {
    flex: 1, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 10, padding: 10, background: "#0b1220",
    color: "#e2e8f0", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace",
    fontSize: 13, lineHeight: 1.45, whiteSpace: "pre-wrap"
  },
  line: { margin: 0, padding: 0 },
  badge: { display: "inline-block", padding: "2px 8px", borderRadius: 999, background: "#e2e8f0", color: "#0f172a", fontSize: 12 },
};
